// src/styles/LandingPageStyles.js
import styled from 'styled-components';
import { StyledButton as CommonStyledButton } from './CommonStyles';
import { Logo as CommonLogo } from './CommonStyles';

// --- Wrapper & Nav ---
export const LandingPageWrapper = styled.div`
    background-color: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.textDark};
    overflow-y: auto;
    overflow-x: hidden;
    display: flex;
    flex-direction: column;
    height: 100%; // Zmienione z min-height: 100vh, aby lepiej współgrać z #root
    width: 100%;
    position: relative;
`;

export const LandingNav = styled.nav`
    position: sticky;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    background-color: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(8px);
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
    width: 100%;
`;

export const LandingContainer = styled.div`
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 ${({ theme }) => theme.spacing.lg};
    width: 100%;

    @media (max-width: 768px) {
        padding: 0 ${({ theme }) => theme.spacing.md};
    }
`;

export const LandingNavContainer = styled(LandingContainer)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 70px;

    @media (max-width: 768px) {
        height: 60px;
    }
`;

export const MobileNavToggle = styled.button`
    display: none;
    background: none;
    border: none;
    cursor: pointer;
    padding: ${({ theme }) => theme.spacing.sm};
    z-index: 1005; // Musi być nad innymi elementami navbara, ale pod menu
    margin-left: auto;

    svg {
        width: 28px;
        height: 28px;
        color: ${({ theme }) => theme.colors.primaryDark};
    }

    @media (max-width: 768px) {
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;

export const LandingNavLinks = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.md};

    a:not([role="button"]) {
        font-family: ${({ theme }) => theme.fonts.headings};
        font-size: 0.95rem;
        font-weight: 600;
        color: ${({ theme }) => theme.colors.textMedium};
        text-decoration: none;
        padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.xs};
        position: relative;
        transition: color 0.2s ease-in-out;

        &:after {
            content: '';
            position: absolute;
            width: 0;
            height: 2px;
            bottom: 0px;
            left: 50%;
            transform: translateX(-50%);
            background-color: ${({ theme }) => theme.colors.primary};
            transition: width 0.3s ease-in-out;
        }

        &:hover {
            color: ${({ theme }) => theme.colors.primary};
            &:after {
                width: 50%;
            }
        }
    }

    @media (max-width: 768px) {
        // Styl dla otwartego menu mobilnego
        opacity: ${({ isOpen }) => (isOpen ? '1' : '0')};
        visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
        transform: ${({ isOpen }) => (isOpen ? 'translateY(0)' : 'translateY(-20px)')}; // Delikatna animacja wysunięcia
        transition: opacity 0.3s ease, visibility 0.3s ease, transform 0.3s ease;
        
        display: flex;
        flex-direction: column;
        position: fixed;
        top: 60px; // Wysokość LandingNavContainer na mobile
        left: 0;
        right: 0;
        // bottom: 0; // Usunięte, wysokość będzie zależeć od zawartości lub max-height
        background-color: ${({ theme }) => theme.colors.backgroundLight};
        padding: ${({ theme }) => theme.spacing.lg}; // Zmniejszony padding, jeśli menu jest krótkie
        // box-shadow: ${({ theme }) => theme.shadows.lg}; // Można dodać cień, jeśli jest potrzebny
        // border-top: 1px solid ${({ theme }) => theme.colors.borderColor}; // Można dodać border
        align-items: center;
        justify-content: flex-start;
        gap: ${({ theme }) => theme.spacing.md}; // Zmniejszony gap między elementami
        
        // Ustawiamy max-height i overflow-y, aby menu było scrollowalne, jeśli treść jest dłuższa niż ekran
        max-height: calc(100vh - 60px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)); // Wysokość ekranu minus navbar i bezpieczne obszary
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        z-index: 1002; // Pod overlayem, ale nad resztą strony

        & > * {
            width: 100%;
            max-width: 300px; // Ogranicz szerokość elementów w menu
            text-align: center;
        }

        a:not([role="button"]), ${CommonStyledButton} {
            padding-top: ${({ theme }) => theme.spacing.sm}; // Mniejszy padding
            padding-bottom: ${({ theme }) => theme.spacing.sm};
            font-size: 1rem; // Nieco mniejszy font w menu mobilnym
            border-radius: ${({ theme }) => theme.borderRadius};
        }

        ${CommonStyledButton} {
            margin-right: 0 !important;
            min-height: 48px; // Dobra wysokość dla dotyku
        }
    }
`;

export const NavAppButton = styled(CommonStyledButton)`
    background: linear-gradient(95deg, ${({ theme }) => theme.colors.primaryLight}, ${({ theme }) => theme.colors.primary});
    color: ${({ theme }) => theme.colors.white};
    border-color: transparent;
    box-shadow: ${({ theme }) => theme.shadows.button};
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
    white-space: nowrap;

    &:hover:not(:disabled) {
        background: linear-gradient(95deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.primaryDark});
        transform: translateY(-2px) scale(1.02);
        box-shadow: 0 6px 16px -2px rgba(2, 132, 199, 0.5);
    }
    &:active:not(:disabled) {
        transform: translateY(0px) scale(0.98);
        box-shadow: 0 2px 8px -1px rgba(2, 132, 199, 0.4);
    }
`;

// --- Sekcje Strony ---
const SectionBase = styled.section`
    padding: ${({ theme }) => theme.spacing.xl} 0;
    overflow-x: hidden;
    width: 100%;
    flex-shrink: 0;

    @media (min-width: 768px) {
        padding: ${({ theme }) => theme.spacing.xxl} 0;
    }
`;

export const HeroSection = styled(SectionBase).attrs({ as: 'header' })`
    text-align: center;
    background: linear-gradient(145deg, ${({ theme }) => theme.colors.primaryLightest} 0%, ${({ theme }) => theme.colors.background} 70%);
    display: flex; // Dodane dla centrowania
    flex-direction: column; // Dodane
    justify-content: center; // Dodane
    min-height: calc(100vh - 70px); // Hero section zajmuje co najmniej wysokość ekranu minus navbar (dla desktopu)
    @media (max-width: 768px) {
        min-height: calc(100vh - 60px); // Dla mobile
        padding-top: ${({ theme }) => theme.spacing.lg}; // Dodatkowy padding na górze na mobile
        padding-bottom: ${({ theme }) => theme.spacing.lg};
    }
`;

export const HeroIconWrapper = styled.div`
    color: ${({ theme }) => theme.colors.primaryLight};
    margin-bottom: ${({ theme }) => theme.spacing.md};
    svg {
        width: 50px; height: 50px; opacity: 0.8;
        @media (min-width: 768px) { width: 60px; height: 60px; }
    }
`;

export const HeroTitle = styled.h1`
    font-family: ${({ theme }) => theme.fonts.headings};
    font-size: 2.25rem; font-weight: 800; color: ${({ theme }) => theme.colors.primaryDarker};
    margin-bottom: ${({ theme }) => theme.spacing.md}; line-height: 1.2;
    span.highlight { color: ${({ theme }) => theme.colors.primary}; }
    @media (min-width: 768px) { font-size: 3rem; }
    @media (min-width: 992px) { font-size: 4rem; line-height: 1.15; }
`;

export const HeroSubtitle = styled.p`
    font-family: ${({ theme }) => theme.fonts.main};
    font-size: 1rem; color: ${({ theme }) => theme.colors.textMedium};
    margin-bottom: ${({ theme }) => theme.spacing.lg}; max-width: 680px;
    margin-left: auto; margin-right: auto; line-height: 1.6;
    @media (min-width: 768px) { font-size: 1.1rem; margin-bottom: ${({ theme }) => theme.spacing.xl}; }
    a { color: ${({ theme }) => theme.colors.primaryDark}; font-weight: 600; text-decoration: underline;
        &:hover { color: ${({ theme }) => theme.colors.primary}; }
    }
`;

export const HeroButton = styled(NavAppButton)`
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
    font-size: 1rem; min-height: 48px;
    svg { margin-left: ${({ theme }) => theme.spacing.xs}; width: 18px; height: 18px; }
    @media (min-width: 768px) { padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl}; font-size: 1.05rem;
        svg { width: 20px; height: 20px; }
    }
`;

export const FeatureSection = styled(SectionBase)`
    background-color: ${({ theme, $alternate }) => $alternate ? theme.colors.neutralLight : theme.colors.backgroundLight};
`;

export const SectionTitle = styled.h2`
    font-family: ${({ theme }) => theme.fonts.headings};
    font-size: 1.8rem; font-weight: 700; color: ${({ theme }) => theme.colors.primaryDark};
    margin-bottom: ${({ theme }) => theme.spacing.md}; text-align: center;
    @media (min-width: 768px) { font-size: 2.25rem; margin-bottom: ${({ theme }) => theme.spacing.lg}; }
    @media (min-width: 992px) { font-size: 2.75rem; }
`;

export const SectionSubtitle = styled.p`
    font-family: ${({ theme }) => theme.fonts.main};
    font-size: 0.95rem; color: ${({ theme }) => theme.colors.textMedium};
    max-width: 700px; margin: 0 auto ${({ theme }) => theme.spacing.lg}; text-align: center;
    @media (min-width: 768px) { font-size: 1.1rem; margin: 0 auto ${({ theme }) => theme.spacing.xl}; }
`;

export const FeatureGrid = styled.div`
    display: grid;
    gap: ${({ theme }) => theme.spacing.md};
    grid-template-columns: 1fr;
    @media (min-width: 600px) { grid-template-columns: repeat(2, 1fr); gap: ${({ theme }) => theme.spacing.lg}; }
    @media (min-width: 992px) { grid-template-columns: repeat(3, 1fr); }
`;

export const ModernFeatureCard = styled.div`
    background-color: ${({ theme }) => theme.colors.white}; padding: ${({ theme }) => theme.spacing.lg};
    border-radius: ${({ theme }) => theme.borderRadius}; box-shadow: ${({ theme }) => theme.shadows.lg};
    text-align: left; transition: transform 0.3s ease, box-shadow 0.3s ease;
    border: 1px solid ${({ theme }) => theme.colors.borderColor}; display: flex; flex-direction: column;
    &:hover { transform: translateY(-6px); box-shadow: ${({ theme }) => theme.shadows.xl}; }
    .icon-wrapper { display: inline-flex; padding: ${({ theme }) => theme.spacing.sm};
        background-color: ${({ theme }) => theme.colors.primaryLightest}; color: ${({ theme }) => theme.colors.primary};
        border-radius: 0.5rem; margin-bottom: ${({ theme }) => theme.spacing.md};
        svg { width: 28px; height: 28px; }
    }
    h3 { font-family: ${({ theme }) => theme.fonts.headings}; font-size: 1.2rem; font-weight: 700;
        color: ${({ theme }) => theme.colors.textDark}; margin-bottom: ${({ theme }) => theme.spacing.xs};
    }
    p { font-family: ${({ theme }) => theme.fonts.main}; font-size: 0.9rem;
        color: ${({ theme }) => theme.colors.textMedium}; line-height: 1.6; flex-grow: 1;
    }
`;

export const PricingSection = styled(SectionBase)`
    background-color: ${({ theme }) => theme.colors.background};
`;

export const PricingGrid = styled.div`
    display: grid; grid-template-columns: 1fr; gap: ${({ theme }) => theme.spacing.lg};
    align-items: stretch; max-width: 380px; margin: 0 auto;
    @media (min-width: 768px) { grid-template-columns: repeat(2, 1fr); max-width: 760px; gap: ${({ theme }) => theme.spacing.xl};}
`;

export const PricingCard = styled.div`
    background-color: ${({ theme }) => theme.colors.white}; padding: ${({ theme }) => theme.spacing.lg};
    border-radius: ${({ theme }) => theme.borderRadius};
    box-shadow: ${({ theme, $featured }) => $featured ? theme.shadows.xl : theme.shadows.lg};
    border: 1px solid ${({ theme, $featured }) => $featured ? theme.colors.primary : theme.colors.borderColor};
    display: flex; flex-direction: column; text-align: center;
    transition: transform 0.3s ease, box-shadow 0.3s ease; position: relative;
    ${({ $featured, theme }) => $featured && `
        @media (min-width: 768px) { transform: scale(1.03); }
        border-width: 2px;
    `}
    &:hover { @media (min-width: 768px) { transform: translateY(-8px) ${({ $featured }) => $featured ? 'scale(1.03)' : 'scale(1)'}; }
        box-shadow: ${({ theme }) => theme.shadows.xl};
    }
    .featured-badge { position: absolute; top: -${({ theme }) => theme.spacing.sm}; left: 50%; transform: translateX(-50%);
        background-color: ${({ theme }) => theme.colors.secondary}; color: ${({ theme }) => theme.colors.white};
        font-family: ${({ theme }) => theme.fonts.headings}; font-size: 0.75rem; font-weight: 700;
        padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
        border-radius: ${({ theme }) => theme.buttonBorderRadius}; z-index: 1;
    }
    h3 { font-size: 1.3rem; font-family: ${({ theme }) => theme.fonts.headings}; font-weight: 700;
        color: ${({ theme }) => theme.colors.primaryDark};
        margin-top: ${({ theme, $featured }) => $featured ? theme.spacing.md : '0'};
        margin-bottom: ${({ theme }) => theme.spacing.sm};
    }
    .price-value { font-size: 2.25rem; font-family: ${({ theme }) => theme.fonts.headings}; font-weight: 800;
        color: ${({ theme }) => theme.colors.textDark}; margin-bottom: ${({ theme }) => theme.spacing.xs};
    }
    .price-period { font-size: 0.85rem; font-family: ${({ theme }) => theme.fonts.main};
        color: ${({ theme }) => theme.colors.textMedium}; margin-bottom: ${({ theme }) => theme.spacing.lg};
    }
    ul { list-style: none; padding: 0; margin: ${({ theme }) => theme.spacing.lg} 0; text-align: left; flex-grow: 1;
        li { display: flex; align-items: center; gap: ${({ theme }) => theme.spacing.sm};
            margin-bottom: ${({ theme }) => theme.spacing.md}; font-family: ${({ theme }) => theme.fonts.main};
            font-size: 0.9rem; color: ${({ theme }) => theme.colors.textMedium};
            svg { color: ${({ theme }) => theme.colors.secondary}; flex-shrink: 0; width: 18px; height: 18px; }
        }
    }
`;

export const PricingButton = styled(CommonStyledButton)`
    width: 100%; padding: ${({ theme }) => theme.spacing.sm} 0;
    font-family: ${({ theme }) => theme.fonts.headings}; font-size: 0.95rem; font-weight: 700; margin-top: auto;
    background-color: ${({ theme, $featured }) => $featured ? theme.colors.primary : theme.colors.secondary};
    border-color: ${({ theme, $featured }) => $featured ? theme.colors.primary : theme.colors.secondary};
    color: ${({ theme }) => theme.colors.white};
    box-shadow: 0 4px 12px -2px ${({ theme, $featured }) => $featured ? 'rgba(2, 132, 199, 0.3)' : 'rgba(20, 184, 166, 0.3)'};
    &:hover:not(:disabled) {
        background-color: ${({ theme, $featured }) => $featured ? theme.colors.primaryDark : theme.colors.secondaryDark};
        border-color: ${({ theme, $featured }) => $featured ? theme.colors.primaryDark : theme.colors.secondaryDark};
        transform: translateY(-2px);
        box-shadow: 0 6px 16px -2px ${({ theme, $featured }) => $featured ? 'rgba(2, 132, 199, 0.4)' : 'rgba(20, 184, 166, 0.4)'};
    }
    &:active:not(:disabled) {
        transform: translateY(0px) scale(0.98);
        box-shadow: 0 2px 8px -1px ${({ theme, $featured }) => $featured ? 'rgba(2, 132, 199, 0.3)' : 'rgba(20, 184, 166, 0.3)'};
    }
`;

export const LandingFooter = styled(SectionBase).attrs({ as: 'footer' })`
    background-color: ${({ theme }) => theme.colors.primaryDarker};
    color: ${({ theme }) => theme.colors.primaryLighter};
    text-align: center;
    margin-top: auto;

    ${CommonLogo} {
        margin-bottom: ${({ theme }) => theme.spacing.md};
        color: ${({ theme }) => theme.colors.white};
        font-size: 1.5rem;
        svg {
            color: ${({ theme }) => theme.colors.secondaryLight};
            width: 30px; height: 30px;
        }
        span {
            color: ${({ theme }) => theme.colors.white};
        }
    }

    p:not(.copyright) {
        font-size: 0.85rem;
        font-family: ${({ theme }) => theme.fonts.main};
        color: ${({ theme }) => theme.colors.primaryLighter};
        margin-bottom: ${({ theme }) => theme.spacing.md};
    }

    .footer-links {
        display: flex;
        justify-content: center;
        flex-wrap: wrap;
        gap: ${({ theme }) => theme.spacing.sm};
        margin-bottom: ${({ theme }) => theme.spacing.md};
        @media (max-width: 768px) {
            flex-direction: column;
            gap: ${({ theme }) => theme.spacing.xs};
        }
        a {
            font-size: 0.8rem;
            font-family: ${({ theme }) => theme.fonts.headings};
            font-weight: 600;
            color: ${({ theme }) => theme.colors.primaryLighter};
            text-decoration: none;
            padding: ${({ theme }) => theme.spacing.xs};
            &:hover {
                color: ${({ theme }) => theme.colors.white};
                text-decoration: underline;
            }
        }
    }

    .copyright {
        font-size: 0.75rem;
        font-family: ${({ theme }) => theme.fonts.main};
        color: ${({ theme }) => theme.colors.primaryLight};
        margin-top: ${({ theme }) => theme.spacing.md};
    }
`;